import { useState, useEffect, useCallback } from "react";
import { listSmsTemplates, createSmsTemplate, updateSmsTemplate, setSmsTemplateDefault, deleteSmsTemplate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Star, Pencil } from "lucide-react";

const PLACEHOLDERS = ["{crew_name}", "{event_name}", "{event_date}", "{venue}"];

export function MessageTemplatesDialog({ open, onOpenChange }) {
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", body: "" });
  const [creating, setCreating] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await listSmsTemplates();
      setTemplates(res.data);
    } catch (err) { toast.error("Failed to load templates"); }
  }, []);

  useEffect(() => { if (open) fetchTemplates(); }, [open, fetchTemplates]);

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setEditForm({ name: "", body: "" });
  };

  const startEdit = (tpl) => {
    setCreating(false);
    setEditingId(tpl.template_id);
    setEditForm({ name: tpl.name, body: tpl.body });
  };

  const cancelEdit = () => { setEditingId(null); setCreating(false); };

  const handleSave = async () => {
    try {
      if (creating) {
        await createSmsTemplate(editForm);
        toast.success("Template created");
      } else {
        await updateSmsTemplate(editingId, editForm);
        toast.success("Template updated");
      }
      setEditingId(null);
      setCreating(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setSmsTemplateDefault(id);
      toast.success("Default template updated");
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSmsTemplate(id);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot delete default template");
    }
  };

  const isEditing = editingId || creating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Message Templates</DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            Customize SMS messages sent to crew. Use placeholders: {PLACEHOLDERS.join(", ")}
          </DialogDescription>
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Template Name</Label>
              <Input
                data-testid="tpl-name-input"
                value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="e.g. Friendly Check"
                className="bg-secondary/30 border-border"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Message Body</Label>
              <textarea
                data-testid="tpl-body-input"
                value={editForm.body}
                onChange={e => setEditForm({ ...editForm, body: e.target.value })}
                placeholder='Hey {crew_name}! Are you available for "{event_name}" on {event_date}{venue}? Reply YES or NO.'
                rows={4}
                className="w-full bg-secondary/30 border border-border rounded-sm p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {PLACEHOLDERS.map(p => (
                <button
                  key={p}
                  className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-sm hover:bg-primary/20"
                  onClick={() => setEditForm({ ...editForm, body: editForm.body + p })}
                >
                  {p}
                </button>
              ))}
            </div>
            {editForm.body && (
              <div className="border border-border rounded-sm p-3 bg-secondary/5">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Preview</p>
                <p className="text-sm">
                  {editForm.body
                    .replace("{crew_name}", "Sam")
                    .replace("{event_name}", "Summer Gala")
                    .replace("{event_date}", "2026-07-15")
                    .replace("{venue}", " at Marble Garden")}
                </p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" className="text-xs" onClick={cancelEdit}>Cancel</Button>
              <Button size="sm" className="text-xs bg-primary text-primary-foreground" onClick={handleSave}>
                {creating ? "Create" : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 py-2">
            <Button
              data-testid="create-template-btn"
              variant="outline"
              size="sm"
              className="w-full text-xs border-dashed border-border"
              onClick={startCreate}
            >
              <Plus className="w-3 h-3 mr-1" /> New Template
            </Button>
            {templates.map(tpl => (
              <div
                key={tpl.template_id}
                data-testid={`tpl-item-${tpl.template_id}`}
                className={`border rounded-sm p-3 ${tpl.is_default ? 'border-primary/40 bg-primary/5' : 'border-border bg-secondary/5'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">{tpl.name}</p>
                    {tpl.is_default && (
                      <Badge variant="secondary" className="text-xs bg-primary/10 text-primary">
                        <Star className="w-2.5 h-2.5 mr-0.5 fill-current" /> Default
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {!tpl.is_default && (
                      <button
                        data-testid={`tpl-set-default-${tpl.template_id}`}
                        className="text-xs text-muted-foreground hover:text-primary px-1.5 py-0.5"
                        onClick={() => handleSetDefault(tpl.template_id)}
                        title="Set as default"
                      >
                        <Star className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      data-testid={`tpl-edit-${tpl.template_id}`}
                      className="text-xs text-muted-foreground hover:text-primary px-1.5 py-0.5"
                      onClick={() => startEdit(tpl)}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!tpl.is_default && (
                      <button
                        data-testid={`tpl-delete-${tpl.template_id}`}
                        className="text-xs text-muted-foreground hover:text-red-400 px-1.5 py-0.5"
                        onClick={() => handleDelete(tpl.template_id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{tpl.body}</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
